/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React examples
import DefaultPricingCard from "./DefaultPricingCard";
import BusinessPricingCard from "./BusinessPricingCard";

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from "context";
import { useTranslation } from "react-i18next";

function PricingCards() {
  const [controller] = useMaterialUIController();
  const { t } = useTranslation("pages");
  const { darkMode } = controller;
  return (
    <MDBox position="relative" zIndex={10} mt={8} px={{ xs: 1, sm: 0 }}>
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} lg={4}>
          <DefaultPricingCard
            color={darkMode ? "dark" : "white"}
            badge={{ color: darkMode ? "warning" : "light", label: t("pricing.freeBadge") }}
            price={{ value: t("pricing.freePrice") }}
            specifications={[
              { label: t("pricing.limitedDataPoint"), highlight: true },
              { label: t("pricing.limitedUploadDataPeriods"), highlight: true },
              { label: t("pricing.environmentDataAllTerms"), highlight: false },
              { label: t("pricing.dataStorageProcessing"), highlight: false },
              { label: t("pricing.performanceForecast"), highlight: false },
              { label: t("pricing.environmentAnalysis"), highlight: false },
              { label: t("pricing.financialViability"), highlight: false },
              { label: t("pricing.emailSupportGeneral"), highlight: false },
            ]}
            action={{
              type: "internal",
              route: "/",
              color: darkMode ? "warning" : "dark",
              label: t("pricing.activate"),
            }}
            shadow={darkMode}
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <BusinessPricingCard
            color="dark"
            badge={{ color: "info", label: t("pricing.business") }}
            specifications={[
              { label: t("pricing.unlimitedDataPoint"), highlight: true },
              { label: t("pricing.unlimitedUploadDataPeriods"), highlight: true },
              { label: t("pricing.environmentDataAllTerms"), highlight: false },
              { label: t("pricing.dataStorageProcessing"), highlight: false },
              { label: t("pricing.performanceForecast"), highlight: false },
              { label: t("pricing.environmentAnalysis"), highlight: false },
              { label: t("pricing.financialViability"), highlight: false },
              {
                label: t("pricing.emailSupportTechnical"),
                highlight: false,
              },
            ]}
            action={{
              type: "internal",
              route: "/",
              color: "info",
              label: t("pricing.activate"),
            }}
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <DefaultPricingCard
            color={darkMode ? "dark" : "white"}
            badge={{ color: darkMode ? "warning" : "light", label: t("pricing.customize") }}
            price={{ value: t("pricing.contact") }}
            specifications={[
              { label: t("pricing.apiIntegration"), highlight: false },
              { label: t("pricing.oemDevelopment"), highlight: false },
              { label: t("pricing.originalAiModel"), highlight: false },
            ]}
            action={{
              type: "internal",
              route: "/",
              color: darkMode ? "warning" : "dark",
              label: t("pricing.contactUs"),
            }}
            shadow={darkMode}
          />
        </Grid>
      </Grid>
    </MDBox>
  );
}

// Typechecking props for the PricingCards
PricingCards.propTypes = {
  prices: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default PricingCards;
